import { useState } from "react";
import { Mail, Loader2, CheckCircle, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import { useTranslation } from "@/i18n/LanguageContext";
import { toast } from "sonner";

export function LoginDialog({ onClose, redirectPath = "/dashboard" }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("choose"); // choose | magic | sent
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);

  const handleGoogleLogin = () => {
    // Always use window.location.origin to support preview, staging and
    // production (bizou.me) domains. Callers must pass a path (e.g. "/create").
    const path = (redirectPath && redirectPath.startsWith("/")) ? redirectPath : "/dashboard";
    const redirect = window.location.origin + path;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) return;
    setSending(true);
    try {
      const response = await fetch(`${API}/auth/magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, redirect_url: redirectPath || "" }),
      });
      if (!response.ok) throw new Error();
      setMode("sent");
    } catch {
      toast.error(t("toast.magicLinkError"));
    } finally {
      setSending(false);
    }
  };

  if (mode === "sent") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="magic-link-sent">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("auth.linkSentTitle")}
          </h2>
          <p className="text-sm text-[#78716C] mb-6">
            {t("auth.linkSentDesc", { email })}
          </p>
          <Button variant="outline" onClick={() => setMode("magic")} className="w-full mb-3" data-testid="resend-link-btn">
            {t("auth.resend")}
          </Button>
          <button onClick={onClose} className="text-sm text-[#78716C] hover:text-[#1C1917]">
            {t("common.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="login-dialog">
        <h2 className="text-xl font-medium text-center text-[#1C1917] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
          {t("common.login")}
        </h2>

        {mode === "choose" && (
          <div className="space-y-3">
            <Button onClick={handleGoogleLogin} className="w-full h-12 rounded-full bg-white border border-[#E7E5E4] text-[#1C1917] font-medium hover:bg-stone-50 shadow-sm" data-testid="google-login-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Google
            </Button>

            <div className="flex items-center gap-3 py-2">
              <div className="flex-1 h-px bg-[#E7E5E4]" />
              <span className="text-xs text-[#78716C]">{t("common.or")}</span>
              <div className="flex-1 h-px bg-[#E7E5E4]" />
            </div>

            <Button onClick={() => setMode("magic")} variant="outline" className="w-full h-12 rounded-full font-medium" data-testid="magic-link-option-btn">
              <Mail className="w-5 h-5 mr-2" />
              {t("auth.magicLinkTitle")}
            </Button>
          </div>
        )}

        {mode === "magic" && (
          <form onSubmit={handleSendMagicLink} className="space-y-4">
            <p className="text-sm text-[#78716C] text-center mb-2">{t("auth.magicLinkDesc")}</p>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                required
                className="pl-10 h-12 rounded-xl"
                data-testid="magic-email-input"
              />
            </div>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("auth.namePlaceholder")}
                className="pl-10 h-12 rounded-xl"
                data-testid="magic-name-input"
              />
            </div>
            <Button type="submit" disabled={sending || !email} className="w-full h-12 rounded-full bg-[#E8175D] text-white font-medium hover:bg-[#C4134F]" data-testid="send-magic-link-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t("auth.sending")}</> : t("auth.sendLink")}
            </Button>
            <button type="button" onClick={() => setMode("choose")} className="w-full text-sm text-[#78716C] hover:text-[#1C1917]">
              {t("auth.orGoogle")}
            </button>
          </form>
        )}

        <button onClick={onClose} className="w-full text-center text-sm text-[#78716C] hover:text-[#1C1917] mt-4">
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}
